import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { NotebookPen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasInstitutionalFeatures } from "@/lib/tier";

const SNOOZE_KEY = "md:debrief-snoozed";
const SNOOZE_MS  = 30 * 60 * 1000; // 30 min — "Skip for now" shouldn't nag again immediately
const LIKERT = [1, 2, 3, 4, 5];
const LIKERT_LABEL = { 1: "Strongly Disagree", 2: "Disagree", 3: "Neutral", 4: "Agree", 5: "Strongly Agree" };

function isSnoozed(positionId) {
    try {
        const raw = JSON.parse(sessionStorage.getItem(SNOOZE_KEY) || "{}");
        return raw[positionId] && (Date.now() - raw[positionId]) < SNOOZE_MS;
    } catch { return false; }
}
function snooze(positionId) {
    try {
        const raw = JSON.parse(sessionStorage.getItem(SNOOZE_KEY) || "{}");
        raw[positionId] = Date.now();
        sessionStorage.setItem(SNOOZE_KEY, JSON.stringify(raw));
    } catch { /* ignore */ }
}

/**
 * PostTradeDebriefModal — Decision Quality Questionnaire (Sept 2026).
 * Obondo/Institutional-lite tiers only. Polls /api/psych-journal/needs-debrief
 * every 60s; backend already gates the tier so a non-gated user simply
 * never receives a pending position here.
 */
export default function PostTradeDebriefModal() {
    const { user } = useAuth() || {};
    const { pathname } = useLocation();
    const [pending, setPending]   = useState(null);
    const [questions, setQuestions] = useState(null);
    const [answers, setAnswers]   = useState({});
    const [busy, setBusy]         = useState(false);
    const userId = user?.id;
    const gated = hasInstitutionalFeatures(user);

    async function poll() {
        if (!gated || !userId || pending) return;
        try {
            const { data } = await api.get("/psych-journal/needs-debrief");
            if (data.position_id && !isSnoozed(data.position_id)) {
                setPending(data);
                const { data: qData } = await api.get("/psych-journal/decision-quality/questions");
                setQuestions(qData.questions);
                setAnswers({});
            }
        } catch (_e) { /* silent */ }
    }

    useEffect(() => {
        if (!gated) return;
        poll();
        const id = setInterval(poll, 60_000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, gated]);

    function closeAndSnooze() {
        snooze(pending.position_id);
        setPending(null); setQuestions(null); setAnswers({});
    }

    const allAnswered = questions && questions.every((q) => answers[q.n]);

    async function submit() {
        if (!allAnswered) return toast.error("Please answer all 10 questions.");
        setBusy(true);
        try {
            const orderedAnswers = questions.map((q) => answers[q.n]);
            const { data } = await api.post("/psych-journal/decision-quality/submit", {
                position_id: pending.position_id,
                trigger:     pending.trigger,
                answers:     orderedAnswers,
                language:    user?.preferred_language || "en",
                pnl_pct:     pending.pnl_pct,
            });
            toast.success(`Decision Quality: ${data.score}/50 · ${data.band.label}`);
            setPending(null); setQuestions(null); setAnswers({});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    }

    // Never surface over the onboarding wizards.
    const onOnboarding = pathname.startsWith("/onboarding/");
    const open = gated && Boolean(pending) && Boolean(questions) && !onOnboarding;
    if (!open) return null;

    const phases = [];
    for (const q of questions) {
        const last = phases[phases.length - 1];
        if (last && last.phase === q.phase) last.items.push(q);
        else phases.push({ phase: q.phase, label: q.phase_label, items: [q] });
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndSnooze(); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="post-trade-debrief-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <NotebookPen className="w-4 h-4 text-primary" /> Decision Quality Questionnaire
                    </DialogTitle>
                    <DialogDescription>
                        {pending.symbol} · PnL {pending.pnl_pct}%
                        {pending.trigger === "layer11_override" && " · Layer 11 override"} —
                        10 questions, 1 (Strongly Disagree) to 5 (Strongly Agree). Logged privately to your discipline history.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-5">
                    {phases.map((phase) => (
                        <div key={phase.phase} className="space-y-3" data-testid={`debrief-phase-${phase.phase}`}>
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                                {phase.label}
                            </div>
                            {phase.items.map((q) => (
                                <div key={q.n} className="border border-border rounded-lg p-3 space-y-2" data-testid={`debrief-question-${q.n}`}>
                                    <p className="text-sm text-foreground">{q.n}. {q.text}</p>
                                    <div className="flex items-center justify-between gap-1">
                                        {LIKERT.map((v) => (
                                            <button
                                                key={v}
                                                type="button"
                                                title={LIKERT_LABEL[v]}
                                                onClick={() => setAnswers((a) => ({ ...a, [q.n]: v }))}
                                                data-testid={`debrief-q${q.n}-answer-${v}`}
                                                className={`flex-1 h-9 rounded-md text-xs font-semibold border transition-colors
                                                    ${answers[q.n] === v
                                                        ? "bg-primary text-primary-foreground border-primary"
                                                        : "bg-secondary/50 border-border text-muted-foreground hover:bg-secondary"}`}
                                            >
                                                {v}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
                                        <span>Strongly Disagree</span>
                                        <span>Strongly Agree</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-1">
                        <Button variant="outline" onClick={closeAndSnooze} disabled={busy}
                                data-testid="debrief-skip">Skip for now</Button>
                        <Button onClick={submit} disabled={busy || !allAnswered}
                                data-testid="debrief-submit">Submit</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
